/**
 * SpendNatureWipe applier.
 *
 * Selective wipe — nature-spend variant (b) from §6.3.
 *
 * - Decrements active player's natureTokens by action.slots.length (1 per slot).
 *   NOTE: spec §6.3 says "Both consume exactly 1 nature token" — re-reading shows
 *   SpendNatureWipe costs exactly 1 nature token total (the player pays 1 to do
 *   any selective wipe, regardless of how many slots they wipe). See spec:
 *   "pick any non-empty subset of slots … the player still must subsequently
 *   draft a paired or decoupled (tile, wildlife). Both consume exactly 1 nature
 *   token." So the cost is 1, not slots.length.
 * - Replaces wildlife tokens in specified slots immediately (immediate wipe per
 *   Cascadia rules — returned to bag, fresh drawn from front of wildlifeBag).
 * - Sets market.optionalWipeUsedThisTurn = true (reuse flag for this turn's
 *   wipe tracking; SpendNatureWipe uses the same "wipe used" flag to prevent
 *   a player doing both optional wipe and nature-spend wipe).
 * - Phase stays "marketCheck".
 *
 * Spec §6.3.
 */

import { resolveWildlifeSpecies } from "../data/instance-resolver";
import type { SpendNatureWipeAction } from "../types/actions";
import type { GameState } from "../types/game-state";
import type { MarketSlot, MarketSlotIndex } from "../types/market";
import { bumpVersion } from "./common-applier";

export const applySpendNatureWipe = (
  state: GameState,
  action: SpendNatureWipeAction,
): GameState => {
  const activeSeat = state.turn.activeSeat;
  const slotsToWipe = new Set(action.slots);

  // Draw fresh wildlife tokens from supply for each wiped slot
  let bagIndex = 0;
  const freshTokens: (string | null)[] = [];
  for (let i = 0; i < action.slots.length; i++) {
    const token = state.supply.wildlifeBag[bagIndex] ?? null;
    freshTokens.push(token);
    bagIndex++;
  }

  // Build slot→freshToken map
  const slotTokenMap = new Map<MarketSlotIndex, string | null>();
  let tokenIdx = 0;
  for (const slotIdx of action.slots) {
    slotTokenMap.set(slotIdx, freshTokens[tokenIdx] ?? null);
    tokenIdx++;
  }

  const newSlots = state.market.slots.map((s) => {
    if (slotsToWipe.has(s.index)) {
      const newToken = slotTokenMap.get(s.index) ?? null;
      return {
        ...s,
        tokenInstanceId: newToken,
        wildlifeSpecies:
          newToken === null ? null : resolveWildlifeSpecies(newToken),
      } satisfies MarketSlot;
    }
    return s;
  }) as [MarketSlot, MarketSlot, MarketSlot, MarketSlot];

  // Remove drawn tokens from front of wildlifeBag
  const newWildlifeBag = state.supply.wildlifeBag.slice(action.slots.length);

  return {
    ...state,
    ...bumpVersion(state),
    market: {
      ...state.market,
      slots: newSlots,
      optionalWipeUsedThisTurn: true,
    },
    supply: {
      ...state.supply,
      wildlifeBag: newWildlifeBag,
    },
    players: state.players.map((p) =>
      p.seat === activeSeat ? { ...p, natureTokens: p.natureTokens - 1 } : p,
    ),
  };
};
